/**
 * Screenshot capture wrapper над html2canvas — opt-in pattern для Block 10
 * «Что улучшить?» auto-screenshot функции.
 *
 * **Lazy-loaded** — html2canvas (~48KB gzipped) импортируется через dynamic
 * import только при реальном вызове `captureScreenshot()`. Initial PWA bundle
 * не утяжеляется для пользователей, которые не используют feedback-форму.
 *
 * **Opt-in (P-009).** Функция throws `ScreenshotNotConsentedError` если
 * параметр `consented !== true`. UI Phase 4 передаёт `consented` из значения
 * чекбокса «Прикрепить скриншот текущего экрана». Без consent — функция не
 * вызывается из UI, но defensive guard страхует от accidental вызовов.
 *
 * **SSR-guard.** Если `typeof window === 'undefined'` (jsdom без `happy-dom`
 * polyfill, SSR build) — throws `ScreenshotNoWindowError`. Защита от
 * Workers / build-time evaluation.
 *
 * **Downscale.** html2canvas рендерит в native pixel ratio; для 4K-экранов
 * результат может быть 4096×3072 (~10MB blob). Wrapper downscale'ит canvas
 * до max **1024×768** перед `.toBlob()` (preserves aspect ratio).
 *
 * **Format.** Output — PNG Blob, quality default (`undefined` для toBlob).
 *
 * References:
 * - ADR-0018 §Q3 (html2canvas opt-in pattern)
 * - ADR-0018 §R1/R4 (bundle impact + jsdom guard)
 * - P-009 (минимизация ПДн — screenshot может содержать интерьеры клиентов)
 */

export const SCREENSHOT_MAX_WIDTH = 1024;
export const SCREENSHOT_MAX_HEIGHT = 768;

export interface CaptureScreenshotOptions {
  /** Обязательное явное согласие пользователя (P-009). UI читает из чекбокса. */
  consented: boolean;
  /** DOM-элемент для захвата. По умолчанию `document.body`. */
  target?: HTMLElement;
  /** Override max width (тестам). По умолчанию `SCREENSHOT_MAX_WIDTH`. */
  maxWidth?: number;
  /** Override max height (тестам). По умолчанию `SCREENSHOT_MAX_HEIGHT`. */
  maxHeight?: number;
}

export class ScreenshotNotConsentedError extends Error {
  constructor() {
    super('SCREENSHOT_NOT_CONSENTED');
    this.name = 'ScreenshotNotConsentedError';
  }
}

export class ScreenshotNoWindowError extends Error {
  constructor() {
    super('SCREENSHOT_NO_WINDOW');
    this.name = 'ScreenshotNoWindowError';
  }
}

export async function captureScreenshot(options: CaptureScreenshotOptions): Promise<Blob> {
  const {
    consented,
    target,
    maxWidth = SCREENSHOT_MAX_WIDTH,
    maxHeight = SCREENSHOT_MAX_HEIGHT,
  } = options;

  if (!consented) {
    throw new ScreenshotNotConsentedError();
  }
  if (typeof window === 'undefined') {
    throw new ScreenshotNoWindowError();
  }

  // Lazy import — keeps html2canvas out of initial bundle.
  const html2canvasModule = await import('html2canvas');
  const html2canvas = html2canvasModule.default;

  const sourceCanvas = await html2canvas(target ?? document.body, {
    logging: false,
    useCORS: true,
  });

  const downscaled = downscaleCanvas(sourceCanvas, maxWidth, maxHeight);

  const blob = await new Promise<Blob | null>((resolve) => {
    downscaled.toBlob((b) => resolve(b), 'image/png');
  });
  if (!blob) {
    throw new Error('SCREENSHOT_TOBLOB_FAILED');
  }
  return blob;
}

function downscaleCanvas(
  source: HTMLCanvasElement,
  maxWidth: number,
  maxHeight: number,
): HTMLCanvasElement {
  const scale = Math.min(1, maxWidth / source.width, maxHeight / source.height);
  if (scale === 1) {
    return source;
  }
  const target = document.createElement('canvas');
  target.width = Math.round(source.width * scale);
  target.height = Math.round(source.height * scale);
  const ctx = target.getContext('2d');
  if (!ctx) {
    throw new Error('SCREENSHOT_CANVAS_CONTEXT_FAILED');
  }
  ctx.drawImage(source, 0, 0, target.width, target.height);
  return target;
}
